import { Field, Label, Select } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Control, Controller } from "react-hook-form";

import { BaseSyntheticEvent, useMemo } from "react";
import { Category } from "../types/Category";
import Checkbox from "./ui/Checkbox";
import RadioGroup from "./ui/RadioGroup";

interface StepOneProps {
  control: Control<any>;
  report_type: string;
  is_competition: boolean;
  nextStep: (
    e?: BaseSyntheticEvent<object, any, any> | undefined
  ) => Promise<void>;
  categories: Category[]; // Accept categories as props
}

const StepOne: React.FC<StepOneProps> = ({
  control,
  is_competition,
  report_type,
  nextStep,
  categories,
}) => {
  const filteredCategories = useMemo(
    () =>
      categories
        .filter((c) => (is_competition ? c.is_competition : !c.is_competition))
        .map((c) => (
          <option value={c.id} key={c.id}>
            {c.name}
          </option>
        )),
    [categories, is_competition]
  );

  return (
    <form onSubmit={nextStep}>
      {/* Report Type Selection - Required */}
      <div className="mb-4">
        <Controller
          control={control}
          name="report_type"
          rules={{ required: true }}
          render={({ field }) => (
            <RadioGroup
              {...field}
              size="lg"
              label="Report Type"
              options={[
                {
                  value: "tableau",
                  label: "Tableau Report",
                },
                {
                  value: "custom",
                  label: "Custom Report",
                },
                {
                  value: "web_link",
                  label: "Web Link",
                },
                {
                  value: "custom_table",
                  label: "Custom Table",
                },
              ]}
            />
          )}
        />
      </div>

      {/* Report URL - Required unless custom/custom_table */}
      {report_type !== "custom" && report_type !== "custom_table" && (
        <>
          <label className="block mb-2">Report URL:</label>
          <Controller
            name="report_url"
            control={control}
            rules={{
              required:
                report_type !== "custom" && report_type !== "custom_table",
            }}
            render={({ field }) => (
              <input {...field} type="url" className="mb-4 p-2 border w-full" />
            )}
          />
        </>
      )}

      {/* Report Name - Required */}
      <label className="block mb-2">Report Name:</label>
      <Controller
        name="report_name"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <input {...field} className="mb-4 p-2 border w-full" />
        )}
      />

      {/* Alias - Required */}
      <label className="block mb-2">Alias:</label>
      <Controller
        name="alias"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <input {...field} className="mb-4 p-2 border w-full" />
        )}
      />

      {/* Description - Required */}
      <label className="block mb-2">
        {report_type === "custom_table" ? "Custom Table Content:" : "Description:"}
      </label>
      <Controller
        name="description"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <textarea {...field} rows={4} className="mb-4 p-2 border w-full" />
        )}
      />

      {/* Tutorial Video Link - Required for Tableau reports */}
      {report_type === "tableau" && (
        <>
          <label className="block mb-2">Tutorial Video Link:</label>
          <Controller
            name="tutorial_video_link"
            control={control}
            rules={{ required: report_type === "tableau" }}
            render={({ field }) => (
              <input {...field} className="mb-4 p-2 border w-full" />
            )}
          />
        </>
      )}

      {/* Competition checkbox - Required for conditional fields */}
      <div className="pt-3">
        <Controller
          name="is_competition"
          control={control}
          render={({ field }) => <Checkbox {...field} label="Competition" />}
        />
      </div>

      {/* Competition-related required fields */}
      {is_competition && (
        <>
          {/* Image URL - Required when is_competition is true */}
          <label className="block mb-2">Image URL:</label>
          <Controller
            name="image_url"
            control={control}
            rules={{ required: is_competition }}
            render={({ field }) => (
              <input {...field} type="url" className="mb-4 p-2 border w-full" />
            )}
          />
          
          {/* Is Featured - Required when is_competition is true */}
          <div className="pt-2">
            <Controller
              name="is_featured_competition"
              control={control}
              rules={{ required: is_competition }}
              render={({ field }) => <Checkbox {...field} label="Featured" />}
            />
          </div>
        </>
      )}

      {/* Category - Required */}
      <Field className="mb-2 pt-4">
        <Label className="">Category:</Label>
        <Controller
          name="category_id"
          rules={{ required: true }}
          control={control}
          render={({ field }) => (
            <div className="relative">
              <Select
                {...field}
                value={field.value || ""}
                className="block mt-2 px-2 py-2 border rounded w-full appearance-none"
              >
                <option className="text-gray-500" disabled value="">
                  No Category Selected
                </option>
                {filteredCategories}
              </Select>

              <ChevronDownIcon className="group top-3 right-2.5 absolute size-4 text-gray-500 pointer-events-none" />
            </div>
          )}
        />
      </Field>

      <button
        type="submit"
        className="bg-blue-500 mt-8 px-4 py-2 rounded w-full md:w-[240px] text-white"
      >
        Next
      </button>
    </form>
  );
};

export default StepOne;
